/* eslint-disable no-await-in-loop */
// 노션에서 직접 가져오기 — notion-import Edge Function 호출 + 루트 적재.
// Phase A: 2-패스 이관 — (1) 페이지/DB 생성 + source_ref 저장 + 노션ID→루트ID 매핑,
//          (2) 본문의 노션 페이지 링크를 /page/루트id 로 재작성 후 ystate 저장.
//          source_ref 로 멱등 처리(재가져오기 시 중복 생성 대신 갱신).

import { supabase } from '../../supabaseClient';
import { markdownToYstate } from './markdownToYstate';

const ws = () => supabase.schema('workspace');
const HEX = /[0-9a-f]{32}/i;
const norm = (id) => String(id || '').replace(/-/g, '').toLowerCase();

// integration 에 공유된 노션 페이지 목록
export async function listNotionPages() {
  const { data, error } = await supabase.functions.invoke('notion-import', { body: { action: 'list' } });
  if (error) throw new Error(error.message || '노션 연결에 실패했습니다.');
  if (!data?.ok) throw new Error(data?.error || '노션 목록을 불러오지 못했습니다.');
  return data.pages || [];
}

// 주어진 페이지(+하위)를 노션에서 가져와 노드 트리 반환
export async function fetchNotionTree(pageId) {
  const { data, error } = await supabase.functions.invoke('notion-import', {
    body: { action: 'import', page_id: pageId },
  });
  if (error) throw new Error(error.message || '노션 페이지를 가져오지 못했습니다.');
  if (!data?.ok) throw new Error(data?.error || '노션 페이지를 가져오지 못했습니다.');
  return data; // { nodes, assets, databases, truncated }
}

// 부모 먼저 정렬(안전망)
// 부모-자식 매칭은 norm() 정규화 키로 한다. 청크(BFS) fetch 한 노드의 parentId(노션 page API
// parent.page_id 는 하이픈 포함) 와 부모 node.id(child_page block id 등) 의 대시·대소문자 차이로
// 매칭이 깨져 자식이 최상위(parent_id null)로 들어가던 버그 방지.
function orderByParent(nodes) {
  const byId = new Map(nodes.map((n) => [norm(n.id), n]));
  const ordered = [];
  const visited = new Set();
  const visit = (n) => {
    if (!n || visited.has(norm(n.id))) return;
    const pk = n.parentId ? norm(n.parentId) : null;
    if (pk && byId.has(pk) && !visited.has(pk)) visit(byId.get(pk));
    visited.add(norm(n.id));
    ordered.push(n);
  };
  nodes.forEach((n) => visit(n));
  return ordered;
}

// 본문 마크다운의 노션 페이지 링크를 /page/루트id 로 재작성.
// 이미지(![]())·업로드 파일 URL 은 제외(자기 노션ID가 경로에 들어가 오인 방지).
function rewriteLinks(md, hexToRoute, titleByHex) {
  if (!md) return md;
  return md.replace(/(!?)\[([^\]]*)\]\(([^)]+)\)/g, (full, bang, text, url) => {
    if (bang === '!') return full;
    if (/workspace-uploads/.test(url)) return full;
    const hm = url.match(HEX);
    if (!hm) return full;
    const hex = hm[0].toLowerCase();
    const route = hexToRoute.get(hex);
    if (!route) return full;
    const t = (text && text !== '페이지') ? text : (titleByHex.get(hex) || '페이지');
    return `[${t}](${route})`;
  });
}

// 링크된 DB 뷰 마커(Phase C) 재작성 → 루트 database id.
// Edge 가 본문에 `:::linkedview collection:<id>:::`(우선) 또는 `:::linkedview notion:<hex>:::`
// 를 남긴다. 노션은 링크뷰를 데이터소스(collection) id 로 원본과 연결하므로 collection 우선.
//  - collection:<id> → collectionToRoute(원본 DB 의 collection) 로 매핑
//  - notion:<hex>    → hexToRoute(db id) 로 매핑(폴백 케이스)
// 매핑되면 `:::linkedview root:<루트dbUuid>:::` 로 정규화, 없으면 마커 유지(렌더 시 폴백 박스).
function rewriteLinkedViews(md, hexToRoute, collectionToRoute) {
  if (!md) return md;
  return md.replace(/:::linkedview (collection|notion):([0-9a-f]{32}):::/gi, (full, kind, id) => {
    const map = kind.toLowerCase() === 'collection' ? collectionToRoute : hexToRoute;
    const route = map.get(norm(id));
    if (!route) return full; // 매핑 없음 → 마커 유지(렌더 시 폴백)
    const rootId = route.replace('/page/', '');
    return `:::linkedview root:${rootId}:::`;
  });
}

/**
 * 노드 배열 → workspace 적재 (2-패스).
 * @param {Array} nodes - [{ id, title, md|columns|rows, parentId, kind }]
 * @param {{ orgId, userId }} ctx
 * @param {(p:{done,total,title})=>void} onProgress
 */
export async function runNotionImport(nodes, ctx, onProgress) {
  const { orgId, userId } = ctx;
  const ordered = orderByParent(nodes);
  const total = ordered.length;
  const errors = [];
  let success = 0; let failed = 0; let contentFailed = 0;
  let positionBase = Date.now();

  const createdIds = new Map(); // notionId -> 루트 page id
  const hexToRoute = new Map(); // normHex -> /page/루트id
  const titleByHex = new Map(); // normHex -> title
  const collectionToRoute = new Map(); // norm(데이터소스 collection id) -> /page/루트dbId
  const pagePass2 = [];         // { rootId, md, title }

  // 멱등: 이미 같은 source_ref 로 가져온 페이지 재사용.
  // 노션 ID 는 대시(-) 유무 두 형태로 들어올 수 있어(중첩 child_database = 대시 / 링크 붙여넣기 = 무대시)
  // norm() 로 정규화해 비교한다. (대시 불일치로 중복 생성되던 버그 방지)
  const existing = new Map();
  try {
    const { data: ex } = await ws().from('pages')
      .select('id, source_ref, title, notion_collection_ref').eq('org_id', orgId).not('source_ref', 'is', null);
    (ex || []).forEach((p) => {
      if (!p.source_ref) return;
      const k = norm(p.source_ref);
      existing.set(k, p.id);
      // 교차-run 매핑: 이전 import 로 만든 페이지/DB 도 링크·링크뷰 대상에 미리 시드한다.
      // (예: 이번에 BUSINESS OS 위키 페이지만 가져와도, 그 안의 링크뷰가 지난 run 에서
      //  이미 적재된 50개 원본 DB 로 연결되게 함. 패스 1 에서 같은 키를 만나면 덮어씀.)
      hexToRoute.set(k, `/page/${p.id}`);
      titleByHex.set(k, p.title || '');
      // 링크뷰는 collection(데이터소스) id 로 연결되므로 별도 시드
      if (p.notion_collection_ref) collectionToRoute.set(norm(p.notion_collection_ref), `/page/${p.id}`);
    });
  } catch (_e) { /* noop */ }

  // OAuth 로 해석한 링크뷰 → 원본 DB 매핑 시드.
  // 노션 통합 API 는 링크뷰(예: "○○ 보기" 데이터베이스)의 원본 데이터소스를 노출하지 않는다
  // (dataSources=[], title="Untitled"). 그래서 OAuth 노션 API 로 해석한 (linkedview_id→루트dbId)
  // 를 notion_linkedview_map 에 저장해 두고, 여기서 hexToRoute 에 시드한다.
  // Edge 가 남긴 `:::linkedview notion:<linkedviewId>:::` 마커가 rewriteLinkedViews 의
  // notion 경로(hexToRoute)로 매핑된다.
  try {
    const { data: lv } = await ws().from('notion_linkedview_map').select('linkedview_id, root_db_id').eq('org_id', orgId);
    (lv || []).forEach((m) => { if (m.linkedview_id && m.root_db_id) hexToRoute.set(norm(m.linkedview_id), `/page/${m.root_db_id}`); });
  } catch (_e) { /* noop */ }

  // ── 패스 1: 페이지/DB 생성 + 매핑 ──
  for (let i = 0; i < ordered.length; i += 1) {
    const node = ordered[i];
    // 부모 매칭은 norm() 정규화 키로 — createdIds 도 norm 키로 저장(아래 .set 참조).
    const parentPageId = node.parentId ? createdIds.get(norm(node.parentId)) || null : null;
    positionBase += 1;
    try {
      const reuseId = existing.get(norm(node.id)) || null;

      if (node.kind === 'database') {
        let dbId = reuseId;
        if (dbId) {
          // 재import 시 기존 부모 보존 — 루트 노드(parentPageId null)가 기존 페이지를 최상위로 끌어올리지 않도록
          const dbUpd = { title: node.title || '제목 없음', notion_collection_ref: node.collectionId || null };
          if (parentPageId) dbUpd.parent_id = parentPageId;
          // 명시적으로 import 한 루트 DB 도 보관(archived) 해제.
          if (!node.parentId) dbUpd.archived = false;
          await ws().from('pages').update(dbUpd).eq('id', dbId);
          const { data: oldRows } = await ws().from('db_rows').select('page_id').eq('database_id', dbId);
          const oldIds = (oldRows || []).map((r) => r.page_id);
          await ws().from('db_rows').delete().eq('database_id', dbId);
          if (oldIds.length) await ws().from('pages').delete().in('id', oldIds);
        } else {
          const { data: dbPage, error: dbErr } = await ws().from('pages').insert({
            org_id: orgId, type: 'database', title: node.title || '제목 없음', icon: '🗂️',
            parent_id: parentPageId, created_by: userId, position: positionBase, source_ref: node.id,
            notion_collection_ref: node.collectionId || null,
          }).select('id').single();
          if (dbErr || !dbPage) throw dbErr || new Error('DB 페이지 생성 실패');
          dbId = dbPage.id;
        }
        createdIds.set(norm(node.id), dbId);
        hexToRoute.set(norm(node.id), `/page/${dbId}`);
        titleByHex.set(norm(node.id), node.title || '');
        // 링크뷰 매칭 키(collection id) 등록 — 이번 run 의 위키 링크뷰가 이 DB 로 연결되게 함
        if (node.collectionId) collectionToRoute.set(norm(node.collectionId), `/page/${dbId}`);
        await ws().from('db_schemas').upsert({ database_id: dbId, columns: node.columns || [] }, { onConflict: 'database_id' });
        await supabase.rpc('ensure_database_defaults', { p_db: dbId });
        let rpos = 0;
        for (const row of (node.rows || [])) {
          const rtitle = (row.properties && row.properties.title) || '제목 없음';
          const { data: rowPage, error: rpErr } = await ws().from('pages').insert({
            org_id: orgId, type: 'db_row', database_id: dbId, title: rtitle, icon: '📄', created_by: userId, position: rpos,
          }).select('id').single();
          rpos += 1;
          if (rpErr || !rowPage) { contentFailed += 1; continue; }
          await ws().from('db_rows').insert({ page_id: rowPage.id, database_id: dbId, properties: row.properties || {} });
        }
        success += 1;
        if (onProgress) onProgress({ done: i + 1, total, title: `🗂️ ${node.title}` });
        continue;
      }

      // 일반 페이지
      let pid = reuseId;
      if (pid) {
        // 재사용 시에도 노션 아이콘을 갱신(노션에서 아이콘이 추가/변경/제거됐을 수 있음).
        // 재import 시 기존 부모 보존 — 서브트리 루트(parentPageId null)가 기존 페이지를 최상위로 끌어올리지 않도록.
        const upd = { title: node.title || '제목 없음', icon: node.icon || null };
        if (parentPageId) upd.parent_id = parentPageId;
        // 명시적으로 import 한 루트 페이지는 보관(archived) 해제 — 보관된 페이지를 다시 import 하면 다시 보이게.
        if (!node.parentId) upd.archived = false;
        await ws().from('pages').update(upd).eq('id', pid);
      } else {
        const { data: page, error: pageErr } = await ws().from('pages').insert({
          // 노션 페이지 아이콘(이모지) 그대로. 없으면 null(노션처럼 아이콘 비움).
          org_id: orgId, type: 'document', title: node.title || '제목 없음', icon: node.icon || null,
          parent_id: parentPageId, created_by: userId, position: positionBase, source_ref: node.id,
        }).select('id').single();
        if (pageErr || !page) throw pageErr || new Error('페이지 생성 결과 없음');
        pid = page.id;
      }
      createdIds.set(norm(node.id), pid);
      hexToRoute.set(norm(node.id), `/page/${pid}`);
      titleByHex.set(norm(node.id), node.title || '');
      pagePass2.push({ rootId: pid, md: node.md, title: node.title });
      success += 1;
    } catch (e) {
      failed += 1; errors.push({ title: node.title, message: e?.message || String(e) });
    }
    if (onProgress) onProgress({ done: i + 1, total, title: node.title });
  }

  // ── 패스 2: 본문 링크 재작성 + ystate 저장 ──
  for (let i = 0; i < pagePass2.length; i += 1) {
    const { rootId, md, title } = pagePass2[i];
    try {
      // 패스 2: (a) 링크된 DB 뷰 마커를 루트 db id 로 정규화 → (b) 페이지 링크 재작성
      const withViews = rewriteLinkedViews(md, hexToRoute, collectionToRoute);
      const fixed = rewriteLinks(withViews, hexToRoute, titleByHex);
      // 재import 중복 방지 — 기존 ystate 를 이어받아 그 계보 안에서 콘텐츠를 교체한다.
      // (fresh 문서로 통째 교체하면 브라우저 IndexedDB 의 구 계보와 병합돼 구+신 중복 렌더.)
      // 조회 실패 시 안전 폴백 = 현행 fresh upsert + 경고.
      let baseYstate = null;
      try {
        const { data: prev, error: prevErr } = await ws().from('page_content')
          .select('ystate').eq('page_id', rootId).maybeSingle();
        if (prevErr) {
          // eslint-disable-next-line no-console
          console.warn('[루트/import] 기존 ystate 조회 실패 — fresh ystate 폴백:', title, prevErr.message);
        } else {
          baseYstate = (prev && prev.ystate) || null;
        }
      } catch (pe) {
        // eslint-disable-next-line no-console
        console.warn('[루트/import] 기존 ystate 조회 예외 — fresh ystate 폴백:', title, pe?.message || pe);
      }
      const ystate = await markdownToYstate(fixed, baseYstate);
      if (ystate) {
        const { error: cErr } = await ws().from('page_content')
          .upsert({ page_id: rootId, ystate, updated_at: new Date().toISOString() }, { onConflict: 'page_id' });
        if (cErr) { contentFailed += 1; errors.push({ title, message: cErr.message }); }
      } else if (md && md.trim()) {
        contentFailed += 1;
        errors.push({ title, message: '본문 변환 실패(폴백: 빈 본문)' });
      }
    } catch (e) {
      contentFailed += 1; errors.push({ title, message: e?.message || String(e) });
    }
  }

  return { success, failed, contentFailed, errors };
}

// 일회성 데이터 보정용 노출 — 브라우저 콘솔에서 기존 페이지 본문/아이콘을 새 변환 규칙(빈 줄 보존·아이콘 매칭)으로
// 재import 할 때 사용한다. 순수 클라이언트 함수(인증 필요)라 사용자 자기 워크스페이스 외 영향 없음.
if (typeof window !== 'undefined') {
  window.__rhootImport = { listNotionPages, fetchNotionTree, runNotionImport };
}
